import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from "flarum/forum/app";
import Component from "flarum/common/Component";
import Button from "flarum/common/components/Button";
import ChatMessage from "./ChatMessage";
import ChatComposer from "./ChatComposer";
import { MessageStreamSkeleton } from "./Skeletons";
import iconLabel from "../utils/iconLabel";
/**
 * A thread's replies, beside the channel.
 *
 * The channel stream shows a thread as one root message with a reply indicator —
 * the API's channel filter drops the replies — so this panel is the only place
 * they are readable. It is a narrower ChannelView: same rows, same composer, but
 * scoped to `filter[thread]` and without the unread divider, since a thread keeps
 * no read marker of its own.
 */
export default class ThreadPanel extends Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "scroller", null);
    _defineProperty(this, "pinned", true);
    _defineProperty(this, "lastRenderedCount", 0);
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.load();
  }
  onbeforeupdate(vnode) {
    var _this$attrs;
    const previousId = (_this$attrs = this.attrs) == null ? void 0 : _this$attrs.threadId;
    super.onbeforeupdate(vnode);

    // Switching threads inside the same instance: reset and reload.
    if (previousId !== undefined && vnode.attrs.threadId !== previousId) {
      this.pinned = true;
      this.lastRenderedCount = 0;
      this.load();
    }
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.scroller = vnode.dom.querySelector(".ChatThreadPanel-stream");
    this.scrollToBottom();
  }
  onupdate() {
    const count = this.attrs.state.threadStream(this.attrs.threadId).messages.length;
    if (count > this.lastRenderedCount && this.pinned) {
      this.scrollToBottom();
    }
    this.lastRenderedCount = count;
  }
  view() {
    const _this$attrs2 = this.attrs,
      channel = _this$attrs2.channel,
      threadId = _this$attrs2.threadId,
      state = _this$attrs2.state,
      onClose = _this$attrs2.onClose;
    const stream = state.threadStream(threadId);
    const thread = app.store.getById("chat-threads", String(threadId));
    return m("div", {
      className: "ChatThreadPanel"
    }, m("div", {
      className: "ChatThreadPanel-header"
    }, m("i", {
      className: "fas fa-comments",
      "aria-hidden": "true"
    }), m("span", {
      className: "ChatThreadPanel-title"
    }, thread ? thread.displayTitle() : app.translator.trans("ramon-chat.forum.thread.title")), m(Button, Object.assign({
      className: "Button Button--icon Button--flat",
      icon: "fas fa-xmark"
    }, iconLabel(app.translator.trans("ramon-chat.forum.thread.close", {}, true)), {
      onclick: onClose
    }))), m("div", {
      className: "ChatThreadPanel-stream",
      onscroll: e => this.onScroll(e)
    }, stream.loading && stream.messages.length === 0 ? MessageStreamSkeleton(4) : null, stream.loadedInitial && stream.messages.length === 0 ? m("div", {
      className: "ChatBrowse-empty"
    }, app.translator.trans("ramon-chat.forum.thread.no_replies")) : null, stream.messages.map((message, index) => {
      var _stream$messages;
      return m(ChatMessage, {
        key: message.id(),
        message: message,
        previous: (_stream$messages = stream.messages[index - 1]) != null ? _stream$messages : null,
        state: state,
        hideThreadIndicator: true,
        onReply: msg => this.reply(msg),
        onEdit: msg => this.edit(msg)
      });
    })), m(ChatComposer, {
      channel: channel,
      state: state,
      threadId: threadId,
      onSent: () => this.onSent()
    }));
  }
  load() {
    const _this$attrs3 = this.attrs,
      state = _this$attrs3.state,
      threadId = _this$attrs3.threadId;

    // The record itself, for the title — the panel can be reached by URL without
    // the channel stream having loaded the root message it hangs off.
    state.findThread(threadId).then(() => m.redraw());
    state.loadThread(threadId).catch(() => {});
  }
  onScroll(e) {
    const el = e.target;
    this.pinned = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (el.scrollTop < 120) {
      const stream = this.attrs.state.threadStream(this.attrs.threadId);
      if (stream.hasMore && !stream.loading) {
        this.attrs.state.fetchThreadPage(this.attrs.threadId).catch(() => {});
      }
    }
  }

  /**
   * Reply and edit are staged against this panel's scope, so the channel composer
   * below is unaffected.
   */
  reply(message) {
    const _this$attrs4 = this.attrs,
      state = _this$attrs4.state,
      channel = _this$attrs4.channel,
      threadId = _this$attrs4.threadId;
    state.setReplyingTo(Number(channel.id()), message, threadId);
    m.redraw();
  }
  edit(message) {
    var _message$content;
    const _this$attrs5 = this.attrs,
      state = _this$attrs5.state,
      channel = _this$attrs5.channel,
      threadId = _this$attrs5.threadId;
    state.setEditing(Number(channel.id()), message, threadId);
    state.setDraft(Number(channel.id()), (_message$content = message.content()) != null ? _message$content : "", threadId);
    m.redraw();
  }
  onSent() {
    this.pinned = true;
    this.scrollToBottom();
  }
  scrollToBottom() {
    if (!this.scroller) return;
    this.scroller.scrollTop = this.scroller.scrollHeight;
  }
}
flarum.reg.add('ramon-chat', 'forum/components/ThreadPanel', ThreadPanel);